const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoianVhbjI2NyIsImEiOiJjbHpsdjM4dHcwNnJhMmxvamR0eHRqa2c3In0.707gQvzDC0UxlvViBusF-g";

const MAPBOX_TILE_URL = `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`;

const MAPBOX_ATTRIBUTION =
  'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>';

const MAX_ZOOM = 22;

export const LOT_LABEL_STYLE = `
  background: rgba(59, 130, 246, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  border: 1px solid rgba(59, 130, 246, 1);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  white-space: nowrap;
  pointer-events: none;
`;

/**
 * Create a Leaflet map with Mapbox satellite tiles.
 * Returns the map instance.
 */
export function createMap(container, { lat = 0, lon = 0, zoom = 13 } = {}) {
  if (container._artemisLeafletMap) {
    container._artemisLeafletMap.remove();
  }

  const map = L.map(container).setView([lat, lon], zoom);
  container._artemisLeafletMap = map;

  map.on("unload", () => {
    if (container._artemisLeafletMap === map) {
      delete container._artemisLeafletMap;
    }
  });

  L.tileLayer(MAPBOX_TILE_URL, {
    maxZoom: MAX_ZOOM,
    attribution: MAPBOX_ATTRIBUTION,
  }).addTo(map);

  return map;
}

export function removeMap(map) {
  if (!map) return;

  map.remove();
}

export function addLayerWhenMapVisible(map, layer) {
  const container = map.getContainer();

  const addLayer = () => {
    if (!map.getContainer()) return true;
    if (container.offsetWidth === 0 || container.offsetHeight === 0) return false;
    if (map.hasLayer(layer)) return true;

    map.invalidateSize({ animate: false });
    layer.addTo(map);
    return true;
  };

  if (addLayer()) return layer;

  if ("ResizeObserver" in window) {
    const observer = new ResizeObserver(() => {
      if (addLayer()) observer.disconnect();
    });
    observer.observe(container);
  } else {
    setTimeout(addLayer, 0);
  }

  return layer;
}

/**
 * Fit map bounds to an array of Leaflet layers, with no animation.
 * Accepts optional padding (default [0, 0]).
 */
export function fitBounds(map, layers, { padding } = {}) {
  if (!layers || layers.length === 0) return;

  const bounds = L.featureGroup(layers).getBounds();
  const opts = { animate: false };
  if (padding) opts.padding = padding;
  map.fitBounds(bounds, opts);
}

/**
 * Generate a label style string for a given hex color.
 */
export function lotLabelStyle(hexColor) {
  const rgb = hexToRgb(hexColor);
  return `
    background: rgba(${rgb}, 0.9);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    border: 1px solid rgba(${rgb}, 1);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    white-space: nowrap;
    pointer-events: none;
  `;
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  }
  return "59, 130, 246";
}

/**
 * Create a styled label marker for a lot polygon.
 * Adds the marker to the map and returns it.
 * Optional color parameter overrides the default blue label style.
 */
export function createLotLabel(map, name, layer, color = null, subtitle = null) {
  const style = color ? lotLabelStyle(color) : LOT_LABEL_STYLE;
  const center = layer.getBounds().getCenter();
  const subtitleHtml = subtitle
    ? `<div style="font-weight: 500; opacity: 0.85">${subtitle}</div>`
    : "";
  return L.marker(center, {
    icon: L.divIcon({
      className: "lot-label",
      html: `<div style="${style}">${name}${subtitleHtml}</div>`,
      iconSize: [null, null],
      iconAnchor: [null, null],
    }),
    pmIgnore: true,
    snapIgnore: true,
  }).addTo(map);
}

// ============================================
// Toggle panel helpers (shared by map controllers)
// ============================================

export const MAP_TOGGLE_ICONS = {
  route: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/></svg>`,
  trees: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-7"/><path d="M5 12l7-9 7 9H5z"/></svg>`,
  fields: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" stroke-dasharray="4 3"/></svg>`,
};

let layerControlStyleInjected = false;

export function injectLayerControlStyle() {
  if (layerControlStyleInjected) return;
  layerControlStyleInjected = true;

  const style = document.createElement("style");
  style.textContent = `
    .leaflet-control-layers-expanded {
      max-height: 50vh;
      overflow-y: auto;
      overflow-x: hidden;
    }
    .leaflet-control-layers-overlays {
      max-height: calc(50vh - 20px);
      overflow-y: auto;
    }
    .leaflet-control-layers-overlays label {
      display: flex;
      align-items: center;
      padding: 2px 0;
    }
  `;
  document.head.appendChild(style);
}

export function createToggleButton(
  container,
  { icon, label, title, activeColor, visible, isFirst, onToggle }
) {
  const btn = L.DomUtil.create("button", "", container);

  btn.style.cssText = `
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 7px 12px;
    border: none;
    border-top: ${isFirst ? "none" : "1px solid #e5e7eb"};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    letter-spacing: 0.01em;
    line-height: 1;
  `;
  btn.innerHTML = `<span style="display:flex;align-items:center">${icon}</span>${label}`;
  btn.title = title;

  let active = visible;

  const update = (isVisible) => {
    active = isVisible;
    btn.style.background = isVisible ? activeColor : "white";
    btn.style.color = isVisible ? "white" : "#9ca3af";
  };

  update(visible);

  btn.onclick = (e) => {
    e.stopPropagation();
    onToggle(update);
  };
  btn.onmouseenter = () => {
    if (!active) btn.style.background = "#f9fafb";
  };
  btn.onmouseleave = () => {
    if (!active) btn.style.background = "white";
  };
  btn.onfocus = () => {
    btn.style.boxShadow = "inset 0 0 0 2px rgba(59, 130, 246, 0.5)";
  };
  btn.onblur = () => {
    btn.style.boxShadow = "none";
  };

  return update;
}

export function createTogglePanelContainer() {
  const container = L.DomUtil.create("div", "leaflet-bar leaflet-control");

  container.style.cssText = `
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    overflow: hidden;
    border: none;
  `;

  return container;
}

// ============================================
// Tree tooltip / navigation helpers (shared by map controllers)
// ============================================

export function hasTooltipValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

export function formatTooltipValue(value, { maxLabel, avgLabel }) {
  const valueString = String(value);
  const match = valueString.match(/^max:\s*([^,]+),\s*avg:\s*(.+)$/i);
  if (!match) return valueString;
  return `${maxLabel}: ${match[1]}, ${avgLabel}: ${match[2]}`;
}

export function openRouteTreeInNewTab(routeTree) {
  if (!routeTree.show_view_url) return;
  window.open(routeTree.show_view_url, "_blank", "noopener");
}

/**
 * Add Geoman controls in read-only mode (all drawing/editing disabled).
 */
export function addReadOnlyGeomanControls(map) {
  map.pm.addControls({
    position: "topleft",
    drawPolygon: false,
    drawMarker: false,
    drawCircleMarker: false,
    drawPolyline: false,
    drawCircle: false,
    drawRectangle: false,
    editMode: false,
    dragMode: false,
    cutPolygon: false,
    drawText: false,
    removalMode: false,
    editControls: false,
  });
}
